import { type FC } from 'react';
import cx from 'classnames';

import { IconPlus } from 'src/assets/icons/Plus';
import { PROJECT_TYPE } from 'src/data/project';

import { IconBlogWizard } from '../../assets/IconBlogWizard';
import { IconImprove } from '../../assets/IconImprove';
import { IconTemplates } from '../../assets/IconTemplates';
import { IconTranslate } from '../../assets/IconTranslate';
import { ActionButton } from '../../components/ActionButton';
import { ProjectTypeDropdown } from '../../components/ProjectTypeDropdown';
import { WIDGET_ACTION, type WidgetActionHandler } from '../../lib';

import styles from './Actions.module.scss';
import { useHandlers } from './useHandlers';
import { useViewState } from './useViewState';

const IMPROVER_PROJECT_TYPE_DESCRIPTIONS = {
  [PROJECT_TYPE.document]:
    'Short text, images, social posts, ads, unstructured emails, product listings and more.',
  [PROJECT_TYPE.longFormBuilder]:
    'Long text, images, blog posts, structured emails, landing pages, case studies, and more.',
};

interface ActionsProps {
  className: string;
  onAction: WidgetActionHandler;
  disabled?: boolean;
}

export const Actions: FC<ActionsProps> = ({ className, onAction, disabled }) => {
  const { blankProjectType, improveProjectType } = useViewState();
  const { handleChangeBlankProjectType, handleChangeImproveProjectType } = useHandlers();

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.actionWrapper}>
        <ActionButton
          title="Blank project"
          Icon={IconPlus}
          tooltip="Start a project from scratch"
          onClick={() => onAction(WIDGET_ACTION.createBlankProject)}
          disabled={disabled}
          customColors={{
            backgroundDefault: 'var(--color-primary-default)',
            backgroundHover: 'var(--color-primary-active)',
            icon: 'var(--color-gray-white)',
          }}
          data-testid="buttonNewBlankProject"
        />

        <ProjectTypeDropdown
          className={styles.projectTypeDropdown}
          value={blankProjectType}
          onChange={handleChangeBlankProjectType}
          buttonTestId="blankProjectTypeDropdownButton"
        />
      </div>

      <ActionButton
        title="Templates"
        Icon={IconTemplates}
        tooltip={'Browse proven templates for campaigns, emails, landing pages, and more'}
        onClick={() => onAction(WIDGET_ACTION.openTemplateGallery)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-primary-light)',
          backgroundHover: 'var(--color-primary-active)',
          icon: 'var(--color-primary-active)',
        }}
        data-testid="buttonTemplateGallery"
      />

      <ActionButton
        title="Blog wizard"
        Icon={IconBlogWizard}
        tooltip={'Generate a complete, SEO/GEO-optimized blog post in minutes with guided steps'}
        onClick={() => onAction(WIDGET_ACTION.createBlogProject)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-pastel-green)',
          backgroundHover: 'var(--color-success-active)',
          icon: 'var(--color-success-active)',
        }}
        data-testid="buttonNewBlogProject"
      />

      <div className={styles.actionWrapper}>
        <ActionButton
          title="Improve content"
          Icon={IconImprove}
          tooltip="Optimize existing content for performance"
          onClick={() => onAction(WIDGET_ACTION.improveContent)}
          disabled={disabled}
          customColors={{
            backgroundDefault: 'var(--color-pastel-pink)',
            backgroundHover: '#D96AA6',
            icon: '#D96AA6',
          }}
          data-testid="buttonImproveContent"
        />

        <ProjectTypeDropdown
          className={styles.projectTypeDropdown}
          value={improveProjectType}
          onChange={handleChangeImproveProjectType}
          customDescriptions={IMPROVER_PROJECT_TYPE_DESCRIPTIONS}
          buttonTestId="improveProjectTypeDropdownButton"
        />
      </div>

      <ActionButton
        title="Translate & localize"
        Icon={IconTranslate}
        tooltip="Adapt your content for different languages and markets"
        onClick={() => onAction(WIDGET_ACTION.createLocalizationProject)}
        disabled={disabled}
        customColors={{
          backgroundDefault: 'var(--color-pastel-yellow)',
          backgroundHover: 'var(--color-warning-active)',
          icon: 'var(--color-warning-active)',
        }}
        data-testid="buttonNewLocalizationProject"
      />
    </div>
  );
};
